/**
 * @file magicRedirect.ts
 * @brief Where a magic link may land: an /app/ path, or exactly the company
 *        cards page /company/billing (the server already restricts it;
 *        checked again here so a bad value can never send the browser
 *        off-site or anywhere else in another role's space).
 *
 * @details
 * /company/billing (20-10-2026, phone payment-free for every account): a
 * company admin whose phone hold needed 3DS is emailed a one-tap link to the
 * company cards page. Exact match only, like BACKEND safeAppRedirect; the
 * page's own role guard still applies after sign-in.
 * 21-10-2026 (BACKEND f5e603e): both cards pages may carry
 * `?confirm=1[&card=pm_…]` from a "confirm with your bank" email, nothing else.
 *
 * 25-10-2026 (B31): a 403 TWO_FACTOR_REQUIRED / TWO_FACTOR_SETUP_REQUIRED is
 * not "blocked": the page asks for the code (or the setup) instead of saying
 * the account may be suspended.
 * 26-10-2026 (B33): after the code, /2fa/verify (or setup-token
 * /setup/confirm) answers `redirect` = the link's page, signed into the
 * pending token by the server. `pageAfterTwoFactor` opens it, checked here
 * again; an older backend sends none, so Plan (or home outside /app).
 *
 * @author DES
 * @date 18-10-2026
 */

export const MAGIC_FALLBACK = "/app/billing/plan";
/** The company cards page, the one non-/app/ landing (20-10-2026). */
export const COMPANY_CARDS = "/company/billing";

/** The two cards pages a "confirm with your bank" email opens (21-10-2026). */
const CONFIRM_CARD_PAGES = new Set(["/app/billing/cards", COMPANY_CARDS]);
/** Their only allowed query: `confirm=1`, optionally `&card=pm_…` (BACKEND f5e603e). */
const CONFIRM_CARD_QUERY = /^confirm=1(&card=pm_[A-Za-z0-9]{1,64})?$/;

export function safeMagicRedirect(path: unknown): string {
  if (typeof path !== "string") return MAGIC_FALLBACK;
  const p = path.trim();
  if (p.startsWith("//") || p.includes("\\") || /[\r\n#]/.test(p)) return MAGIC_FALLBACK;
  const q = p.indexOf("?");
  const pathname = q === -1 ? p : p.slice(0, q);
  // The cards pages take no query but ?confirm=1[&card=pm_…], as the server.
  if (CONFIRM_CARD_PAGES.has(pathname)) {
    return q === -1 || CONFIRM_CARD_QUERY.test(p.slice(q + 1)) ? p : MAGIC_FALLBACK;
  }
  if (!p.startsWith("/app/") && p !== "/app") return MAGIC_FALLBACK;
  return p;
}

/** The link's outcome from the verify call's HTTP status / code. */
export function magicFailureKind(
  status: number,
  code: string | undefined,
): "expired" | "blocked" | "twoFactor" | "error" {
  if (code === "TWO_FACTOR_REQUIRED" || code === "TWO_FACTOR_SETUP_REQUIRED") return "twoFactor";
  if (status === 410 || code === "MAGIC_LINK_EXPIRED" || status === 400 || status === 404) return "expired";
  if (status === 403) return "blocked";
  return "error";
}

/**
 * Where the one-tap link page goes after the code step (B33): the answer's
 * `redirect` when it passes `safeMagicRedirect`, else Plan if this account
 * lives under /app, else its home.
 */
export function pageAfterTwoFactor(redirect: string | null | undefined, allowedPrefix: string, home: string): string {
  if (typeof redirect === "string" && redirect.trim()) {
    const safe = safeMagicRedirect(redirect);
    if (safe === redirect.trim()) return safe;
  }
  return MAGIC_FALLBACK.startsWith(allowedPrefix) ? MAGIC_FALLBACK : home;
}
